import clsx from 'clsx'
import { MapPin, Gauge, Timer, Truck } from 'lucide-react'
import { fenceStyle } from '../map/layers/fenceIcons'

export interface GeofenceCardProps {
  name: string
  type: string
  hubCode?: string | null
  inside: number
  speedLimitKph?: number | null
  tatMinutes?: number | null
  areaKm2?: number | null
  selected?: boolean
  onClick?: () => void
  className?: string
}

/**
 * One corridor place in the side panel — type colour matches the map badge so the
 * list and the map read as the same vocabulary.
 */
export function GeofenceCard({
  name,
  type,
  hubCode,
  inside,
  speedLimitKph,
  tatMinutes,
  areaKm2,
  selected,
  onClick,
  className,
}: GeofenceCardProps) {
  const style = fenceStyle(type)
  const occupied = inside > 0

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={clsx(
        'relative block w-full overflow-hidden rounded-[12px] border text-left transition-all',
        'hover:-translate-y-px hover:shadow-[0_2px_6px_rgba(15,23,42,0.08)] motion-reduce:hover:translate-y-0',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]',
        selected
          ? 'border-[#2563EB] bg-[#EFF6FF] shadow-[0_0_0_1px_rgba(37,99,235,0.25)]'
          : occupied
            ? 'border-[rgba(37,99,235,0.22)] bg-[#F8FBFF]'
            : 'border-[rgba(15,23,42,0.07)] bg-white hover:border-[rgba(15,23,42,0.16)]',
        className,
      )}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ backgroundColor: style.color }}
      />
      <div className="flex gap-3 p-3 pl-3.5">
        <span
          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-[10px] font-bold uppercase tracking-wide text-white"
          style={{ backgroundColor: style.color }}
          title={style.hint}
        >
          {style.label.slice(0, 2)}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold text-[#0F172A]">{name}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[#64748B]">
                <span className="font-medium" style={{ color: style.color }}>{style.label}</span>
                {hubCode ? (
                  <span className="inline-flex items-center gap-0.5 font-mono text-[#94A3B8]">
                    <MapPin size={10} strokeWidth={2.2} />
                    {hubCode}
                  </span>
                ) : null}
              </div>
            </div>

            <div
              className={clsx(
                'flex shrink-0 flex-col items-end rounded-[8px] px-2 py-1',
                occupied ? 'bg-[#2563EB] text-white' : 'bg-[#F1F5F9] text-[#94A3B8]',
              )}
              title={occupied ? `${inside} truck${inside === 1 ? '' : 's'} inside` : 'Empty'}
            >
              <span className="flex items-center gap-1 font-mono text-[14px] font-semibold tabular-nums leading-none">
                <Truck size={12} strokeWidth={2.4} className={occupied ? 'opacity-90' : 'opacity-50'} />
                {inside}
              </span>
              <span className={clsx('mt-0.5 text-[9px] uppercase tracking-wider', occupied ? 'text-white/70' : 'text-[#94A3B8]')}>
                inside
              </span>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {speedLimitKph != null && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#F8FAFC] px-2 py-0.5 text-[10px] font-medium text-[#475569] ring-1 ring-[rgba(15,23,42,0.06)]">
                <Gauge size={10} strokeWidth={2.2} />
                {Math.round(speedLimitKph)} KMPH
              </span>
            )}
            {tatMinutes != null && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#F8FAFC] px-2 py-0.5 text-[10px] font-medium text-[#475569] ring-1 ring-[rgba(15,23,42,0.06)]">
                <Timer size={10} strokeWidth={2.2} />
                TAT {tatMinutes}m
              </span>
            )}
            {areaKm2 != null && areaKm2 > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#F8FAFC] px-2 py-0.5 text-[10px] font-medium text-[#94A3B8] ring-1 ring-[rgba(15,23,42,0.06)]">
                {areaKm2 < 0.01 ? `${Math.round(areaKm2 * 1e6)} m²` : `${areaKm2.toFixed(2)} km²`}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}
